/**
 * CR-7277 — whether a PO line actually has a change to accept or reject.
 *
 * The screens used to infer this from the presence of three *display* values,
 * which is how an empty state came to present as a change request: a line
 * whose `acknowledge_status` said `change_proposed_*` while every
 * `supplier_proposed_*` field was empty rendered "A change has been proposed"
 * next to live Accept and Reject buttons. Accepting there does real damage —
 * the accept path falls through to `supplier_promise_date` and writes it over
 * the line's agreed date, which re-anchors OTIF and the supplier's scorecard.
 *
 * So the question "is there something to act on?" gets one answer, used by
 * every surface that offers an action, rather than each one re-deriving it.
 *
 * Note the deliberate difference from what the UI *renders*: a supplier who
 * proposes the date the line already carries writes a real payload that the
 * screen has nothing to show for. That is a payload (so the status is not
 * stale) but not a change (so there is nothing to display or agree). The two
 * predicates below keep those apart.
 */

/** The subset of a PO line this module needs. */
export interface ProposalFields {
  acknowledge_status?: string | null;
  /** Orthogonal to the acknowledge status: a held record is negotiable by nobody. */
  on_hold?: boolean | null;
  supplier_proposed_date?: string | null;
  supplier_proposed_qty?: string | number | null;
  supplier_proposed_unit_price?: string | number | null;
  /** R-378: the SERVER's grain-aware ODD (purchasing.line_odd). Read this
   *  first — re-deriving the ODD here gets schedule-grain customers wrong,
   *  because at that grain the anchor is the slot date, not the stored
   *  original_required_date. */
  odd_date?: string | null;
  original_required_date?: string | null;
  original_promise_date?: string | null;
  customer_required_date?: string | null;
  delivery_date?: string | null;
  /** The serializer's whose-move answer. On a `change_declined` line it names
   *  the side whose request was declined. */
  waiting_on?: 'Customer' | 'Supplier' | null;
  /** The line's receipt status; a finished line takes no Acknowledge Decline. */
  status?: string | null;
}

/** Statuses that claim a change is pending. */
export const CHANGE_PROPOSED_STATUSES = [
  'change_proposed_supplier',
  'change_proposed_customer',
] as const;

export function isChangeProposedStatus(status?: string | null): boolean {
  return !!status && (CHANGE_PROPOSED_STATUSES as readonly string[]).includes(status);
}

/**
 * Customer review 11 Sep (item 28): may a screen present this line as
 * carrying an open change request at all?
 *
 * Only the workflow status can say so. Every propose path in
 * POLineWorkflowService (propose_alternative_date / propose_quantity_change /
 * propose_price_change) writes `change_proposed_*` in the same save as the
 * proposed value, and every accept / reject / cancel clears the value before
 * it resolves the status. A genuine proposal therefore always carries the
 * status. A `supplier_proposed_*` value on a line in any other status is
 * residue: on ERP-import tenants it is the legacy `SupplierProposeDate`
 * column, populated on 94,055 of the reviewed tenant's 96,097 lines, all of them `new` or
 * `wip` -- which is how every line came to show "Active Change Request, Open,
 * Awaiting Decision, Unknown" with Accept / Reject.
 *
 * A `new` line with nothing proposed is still awaiting the supplier's
 * acknowledgement (accept_delivery_date's initial accept), so the header's
 * "Waiting on Supplier" stays -- that is an acknowledgement owed, not a change
 * to decide. The CR-4882 / CR-7277 guards below still govern a
 * `change_proposed_*` row whose payload is missing or a no-op.
 *
 * @param line - The line's workflow fields.
 * @returns True when the status says a change is pending.
 */
export function hasOpenChangeRequest(line: ProposalFields): boolean {
  return isChangeProposedStatus(line.acknowledge_status);
}

/**
 * Does the line carry any proposed value at all?
 *
 * This is the safety question. It ignores whether the value differs from what
 * the line already holds — a same-date proposal still counts, because the
 * status is then backed by something the user really sent.
 */
export function hasProposalPayload(line: ProposalFields): boolean {
  return Boolean(
    line.supplier_proposed_date ||
    line.supplier_proposed_qty ||
    line.supplier_proposed_unit_price,
  );
}

/**
 * A line whose status claims a pending change but which carries no proposed
 * value to back it. Nothing may act on one of these.
 */
export function isPhantomChangeRequest(line: ProposalFields): boolean {
  return isChangeProposedStatus(line.acknowledge_status) && !hasProposalPayload(line);
}

/**
 * A payload that exists but proposes the date the line already has, so there
 * is nothing to show and nothing to agree. Distinguished from a phantom
 * because the cause and the wording differ: this one is a real submission the
 * write path should have refused.
 */
export function isNoOpDateProposal(line: ProposalFields): boolean {
  if (!isChangeProposedStatus(line.acknowledge_status)) return false;
  if (line.supplier_proposed_qty || line.supplier_proposed_unit_price) return false;
  const to = line.supplier_proposed_date || '';
  // R-378: the server's grain-aware ODD wins; the stored columns are only a
  // fallback for a payload that predates odd_date, and the deprecated key
  // names ride last for the contract-rename window.
  const from = line.odd_date || line.original_required_date || line.customer_required_date
    || line.original_promise_date || line.delivery_date || '';
  return Boolean(to && from && to === from);
}

/**
 * May this line's change be accepted or rejected?
 *
 * The gate for every Accept / Reject control, on the line modal and on the
 * list's bulk actions alike. False for a phantom and for a no-op proposal:
 * in both cases the screen has nothing to show the user, so it must not offer
 * them a button either.
 */
export function canActOnProposal(line: ProposalFields): boolean {
  return (
    isChangeProposedStatus(line.acknowledge_status) &&
    hasProposalPayload(line) &&
    !isNoOpDateProposal(line)
  );
}

/* ------------------------------------------------------------------ *
 * WHO may act, and WHEN — the one rule, for all four surfaces.
 *
 * EXPEDITING-PARITY PR 2, 17 Sep 2026. Before this, four surfaces answered
 * "may I accept or reject this?" four different ways:
 *   - the PO Lines grid (usePOLinesActions) said a declined change is resolved
 *     and offered nothing on it;
 *   - the line modal (usePOLineDetail) offered Accept/Reject to the side that
 *     had just DECLINED the change;
 *   - the schedule row and the schedule modal offered it to the side whose
 *     proposal was declined;
 *   - and legacy (OpenOrders.js canUserActOnPOLine) offered it to whoever
 *     RAISED it, keyed on PreAcknowledgeStatus.
 * Aaron's ruling (17 Sep, option C): a decline may carry a COUNTER, and with
 * one it is not a decline at all — it goes through the propose path and lands
 * on change_proposed_<the decliner>, waiting on the other side. Without a
 * counter the request is closed, and the only way forward is a fresh proposal,
 * from either side. So `change_declined` means exactly one thing and carries
 * exactly one action.
 *
 * Two things every surface asks, answered here so they cannot drift:
 *   isHeld()            — a held record is out of the negotiation entirely
 *                         (legacy bails on row.OnHold before anything else;
 *                         PR 1 made the server refuse it in
 *                         expediting_rules.assert_open_for_negotiation).
 *   mayReviewProposal() — accept or reject, which is the counterparty's and
 *                         nobody else's. Mirrors the server's
 *                         counterparty_of(raiser_of(status)).
 * What stays with each surface is its own ENVIRONMENT: feature flags, the
 * per-site write permission, call-off warehouse cover, collab-closed statuses.
 * ------------------------------------------------------------------ */

/** Which side of the table a user is acting as. */
export type ExpeditingSide = 'customer' | 'supplier';

/** A held line or schedule row is negotiable by nobody until it is released. */
export function isHeld(record: ProposalFields): boolean {
  return Boolean(record.on_hold);
}

/** The side that raised the pending proposal, or null when nothing is pending. */
export function raiserOfProposal(status?: string | null): ExpeditingSide | null {
  if (status === 'change_proposed_supplier') return 'supplier';
  if (status === 'change_proposed_customer') return 'customer';
  return null;
}

/**
 * May `side` accept or reject what is on this record right now?
 *
 * True only for the COUNTERPARTY of a live proposal: something must be
 * pending (canActOnProposal — not a phantom, not a no-op), the record must not
 * be held, and the asker must not be the side that raised it. `change_declined`
 * is false for both sides by construction — nothing is pending on it.
 *
 * Not covered here, deliberately: the supplier's acknowledgement of a `new`
 * line. It shares the Accept button but it is not a proposal review — it is the
 * supplier answering the order — so each surface states it in one line of its
 * own rather than hiding it inside this predicate.
 */
export function mayReviewProposal(record: ProposalFields, side: ExpeditingSide | null): boolean {
  if (!side) return false;
  if (isHeld(record)) return false;
  if (!canActOnProposal(record)) return false;
  return raiserOfProposal(record.acknowledge_status) !== side;
}

/** The statuses on which a fresh proposal may be raised, by either side. */
const PROPOSABLE_STATUSES = ['new', 'accepted', 'wip', 'change_declined'] as const;

/**
 * May `side` raise a NEW date/qty proposal on this record right now?
 *
 * Nothing pending, nothing held. The deliberate exclusion is
 * `change_proposed_*`: while a request is open it gets ANSWERED (accept or
 * decline), never proposed over. That is the schedule grain's rule and the
 * server enforces it in
 * ``ScheduleWorkflowService._assert_no_counter_over_pending``, whose refusal
 * names the side holding the request.
 *
 * It differs from the PO-LINE grain, where a counter-proposal over the other
 * side's pending request IS allowed (CR-7166, `assert_may_propose`). The
 * schedule grain has no per-field lock to name a raiser per field, so a
 * counter there could re-label a pending quantity as the counter-proposer's
 * own and let its raiser accept it — which is why that grain refuses.
 *
 * Stated here, once, because the row and the modal previously answered it
 * separately: the modal had this list, the row additionally offered the
 * OTHER side's `change_proposed_*`, so the supplier was shown Propose Date /
 * Propose Qty over a customer's live request, filled the form, and got the
 * server's refusal on submit.
 *
 * @param record - The record's workflow fields.
 * @param side - The side asking, or null for neither.
 * @returns True when a fresh proposal may be raised.
 */
export function mayProposeChange(record: ProposalFields, side: ExpeditingSide | null): boolean {
  if (!side) return false;
  if (isHeld(record)) return false;
  return (PROPOSABLE_STATUSES as readonly string[]).includes(record.acknowledge_status || '');
}

/**
 * May `side` resolve the declined request on this line (EXPEDITING-3, CR-8718)?
 *
 * Only on `change_declined`, only the side whose request was declined, never on
 * a held or finished line. The raiser is read from `waiting_on`, which the
 * serializer derives from the same field the server's `close_declined_request`
 * checks. (A helix operator may close for either side on the server; the screen
 * offers it to the raising side only.)
 *
 * CR-8718: true is what sends that side to Review Changes, and what puts
 * Acknowledge Decline and Amend Request on the Change Requests tab. The name
 * keeps the endpoint's (`close-declined-request`).
 *
 * @param record - The line's workflow fields.
 * @param side - The side asking, or null for neither.
 * @returns True when Acknowledge Decline / Amend Request are theirs.
 */
export function mayCloseDeclinedRequest(record: ProposalFields, side: ExpeditingSide | null): boolean {
  if (!side || isHeld(record)) return false;
  // Round 1: the server refuses a closed line (received / cancelled), so the
  // badge and the button must not offer one.
  if (record.status === 'received' || record.status === 'cancelled') return false;
  if (record.acknowledge_status !== 'change_declined') return false;
  return (record.waiting_on || '').toLowerCase() === side;
}

/**
 * How many changes are waiting for `side` to deal with (EXPEDITING-3, the
 * Change Requests tab badge). A declined request of `side`'s own, which it may
 * acknowledge or amend, counts as one; otherwise each field of the other side's live request
 * counts one.
 *
 * @param record - The line's workflow fields.
 * @param side - The viewer's side, or null for neither.
 * @returns The count; 0 when nothing is theirs to answer.
 */
export function pendingActionCount(record: ProposalFields, side: ExpeditingSide | null): number {
  if (mayCloseDeclinedRequest(record, side)) return 1;
  if (!mayReviewProposal(record, side)) return 0;
  return [record.supplier_proposed_date, record.supplier_proposed_qty, record.supplier_proposed_unit_price]
    .filter((v) => v !== null && v !== undefined && v !== '').length;
}
